import {BaseError} from 'make-error'

import isFunction from 'lodash/isFunction'
import {all, delay, put, select, take, takeLatest} from 'redux-saga/effects'

import history from '../history'

import {forUserAndEvent} from './login'
import {getAllEventsIfNotExist} from './event'
import {getFitProfileIfNotExist} from './fitProfile'
import {getSizesIfNotExist} from './sizes'
import {leadingThrottle} from './helpers'
import {
  CALCULATE,
  CALCULATE_FAILURE,
  EDIT_SIZES,
  EDIT_SIZES_FAILURE,
  ENTER_FIT_FLOW,
  LEAVE_FIT_FLOW,
  SET_ON_DECK_STEP,
  SET_ON_DECK_STEP_FAILURE,
  START,
  SUBMIT_CURR_STEP_AND_CALCULATE,
  SUBMIT_STEP,
  calculate,
  calculateFailure,
  calculatePending,
  calculateSuccess,
  cancelSizes,
  displayError,
  editSizesFailure,
  recordStepResponse,
  recordSurveyInputValidationFailure,
  setCurrStep,
  setOnDeckStepFailure,
} from '../actions/fit'
import {
  SET_FIT_PROFILE_FAILURE,
  SET_FIT_PROFILE_SUCCESS,
  setFitProfile,
} from '../actions/fitProfile'
import {CLOSE_ONBOARDING, initiateOnboarding} from '../components/Onboarding/redux/actions'
import {FIT_STEPS} from '../components/Fit/enums'
import {
  FitCalculateError,
  FitCannotChangeMeasurementsError,
  FitStepGatedError,
  FitStepDoesNotExistError,
  UserIsNotAuthenticatedError,
  ValidationError,
} from '../exceptions'
import * as cross from '../store/cross'
import * as events from '../store/events'
import * as fit from '../store/fit'
import * as user from '../store/user'
import * as siteActions from '../actions/site'
import * as track from '../track/track'
import * as URLS from '../urls'
import {isFilledString} from '../utils'

// Enter
// ----------------------------------------------------------------------------

export function* enterFitFlowWorker() {
  yield put(siteActions.hideFooter())
}

export function* enterFitFlowSaga() {
  yield takeLatest(ENTER_FIT_FLOW, enterFitFlowWorker)
}

// Leave
// ----------------------------------------------------------------------------

export function* leaveFitFlowWorker() {
  yield put(siteActions.showFooter())
}

export function* leaveFitFlowSaga() {
  yield takeLatest(LEAVE_FIT_FLOW, leaveFitFlowWorker)
}

// Start
// ----------------------------------------------------------------------------

function* startWorker() {
  try {
    const state = yield select()

    // analytics
    track.startedFitFlow({
      customerType: cross.customerType(state),
      flow: 'Fit Survey',
    })

    if (user.isAuthenticated(state)) {
      history.push(fit.nextStepUrl(state))
    } else {
      // NOTE: We pass in a dummy event to satisfy the first parameter
      //  of this function.
      // NOTE: We pass in a dummy function to trigger a code path which
      //  displays "Save & Continue" as the button text and doesn't do
      //  anything weird when you click it; we need more control over
      //  the outcome, so we'll handle it in the worker

      // start onboarding
      yield put(initiateOnboarding(() => {}))
      // wait for onboarding to close (this means the user clicked out or
      //  completed onboarding or something)
      yield take(CLOSE_ONBOARDING)
      // if the user is authenticated
      // (note that we're using the latest state here)
      if (yield select(user.isAuthenticated)) {
        // try to send them to the Your Sizes page
        // don't worry, if the user doesn't have sizes, they'll get sent
        //  back to the appropriate step
        window.location.href = URLS.FIT_SIZES
      }
    }
  } catch (e) {
    console.error(e)
  }
}

export function* startSaga() {
  yield takeLatest(START, startWorker)
}

// Set On Deck & Current Steps
// ----------------------------------------------------------------------------

function* setOnDeckStepWorker({payload}) {
  if (window.location.pathname.indexOf(URLS.FIT) === 0) {
    // make sure that we're in the fit flow--this was causing issues on logout
    //  as this worker would be activated and change the current url to land
    //  back under /fit, instead of continuing navigation to the homepage

    const slug = payload

    try {
      /* BEGIN LOGIC WHICH DOESN'T DEPEND ON STATE OF USER AUTHENTICATION */

      const state1 = yield select()

      // go to intro step if slug is missing or invalid
      if (!fit.slugExists(state1, slug)) throw new FitStepDoesNotExistError(URLS.FIT_INTRO)

      /* BEGIN LOGIC WHICH DEPENDS ON STATE OF USER AUTHENTICATION */

      // wait for authentication; aww yeah
      const state2 = yield forUserAndEvent()

      const step = fit.getStepBySlug(state2, slug)

      // here we check if the user is authenticated
      if (user.isAuthenticated(state2)) {
        // if they are, we can make sure that sizes and fit profile exist, and proceed normally
        yield all([getFitProfileIfNotExist(), getSizesIfNotExist(), getAllEventsIfNotExist()])
      } else if (step === FIT_STEPS.INTRO) {
        // if not, but they're accessing the intro page, that's fine; let them through
      } else {
        // otherwise, throw an error which redirects them to the intro page
        throw new UserIsNotAuthenticatedError(URLS.FIT_INTRO)
      }

      // get the state again, which potentially contains the new fit profile and sizes
      const state3 = yield select()

      // gating
      const redirect = isFunction(step.gate) && step.gate(state3, step)
      if (redirect) throw new FitStepGatedError(redirect)

      // 30 day block
      if (fit.stepIsInSurvey(step) && !cross.canChangeMeasurements(state3)) {
        throw new FitCannotChangeMeasurementsError(events.soonestUpcomingPaidEventDate(state3))
      }

      // okay, we can go to the step now
      yield put(setCurrStep(fit.onDeckStepSlug(state3)))
    } catch (e) {
      if (e instanceof FitStepGatedError) {
        // user can keep that step in their browser history
        history.push(e.redirect)
      } else if (
        e instanceof FitStepDoesNotExistError ||
        e instanceof UserIsNotAuthenticatedError
      ) {
        // let's pretend like they never went there
        history.replace(e.redirect)
      } else {
        yield put(setOnDeckStepFailure(e))

        if (e instanceof FitCannotChangeMeasurementsError) {
          history.replace(URLS.FIT_SUMMARY)
        } else {
          console.error(`Could not progress current step to on deck step. Reason: ${e.message}`)
        }
      }
    }
  }
}

export function* setOnDeckStepSaga() {
  yield takeLatest(SET_ON_DECK_STEP, setOnDeckStepWorker)
}

// Step Submit
// ----------------------------------------------------------------------------

class DoubleSubmit extends BaseError {}

function* submitWorker({type, payload}) {
  try {
    const currStep = yield select(fit.currStep)
    let source
    let step

    if (type === SUBMIT_STEP) {
      ;({source, step} = payload)
      if (step !== currStep) throw new DoubleSubmit() // ex. user double-clicked
    } else if (type === SUBMIT_CURR_STEP_AND_CALCULATE) {
      source = 'calculate'
      step = currStep
    }

    const input = yield select(fit.getStepInputById, step.id)
    let readable
    let validatable

    if (isFilledString(input)) {
      // if we have a real input recorded, turn it into an object
      const object = step.type.keyToObject(input)
      readable = object.toReadable() // and get the readable
      validatable = object.toValidatable() // and and validatable forms
    }

    // analytics
    track.fitSurveyStepSubmitted(readable, source)

    // check for validation errors
    step.validate(validatable)
    // record the user's input as a response
    yield put(recordStepResponse(step.id, input))
    // go to the next step, maybe
    if (type === SUBMIT_CURR_STEP_AND_CALCULATE) {
      yield put(calculate())
    } else if (step !== (yield select(fit.finalSurveyStep))) {
      history.push(yield select(fit.nextStepUrl))
    } else {
      // do nothing more; wait for user to trigger SUBMIT_AND_CALCULATE
    }
  } catch (e) {
    if (e instanceof ValidationError) {
      // if there is a validation error, record it
      yield put(recordSurveyInputValidationFailure(e.message))
    }
    if (e instanceof DoubleSubmit) {
      console.warn('Prevented step from being double-submitted.')
    } else {
      console.error(e.message)
    }
  }
}

export function* submitSaga() {
  yield leadingThrottle(300, [SUBMIT_STEP, SUBMIT_CURR_STEP_AND_CALCULATE], submitWorker)
}

// Calculate
// ----------------------------------------------------------------------------

function* calculateWorker() {
  try {
    const startTime = Date.now()
    yield put(calculatePending())

    const storedFitProfile = yield select(fit.stepResponsesById)

    // Set measuredAt to null because if the user previously had an in-store fitting, but then came
    // back through the Fit Survey, we need to set their measuredAt to null as their new sizes
    // will be the result of predictions, not in-store fitting.
    const fitSurveyProfile = {...storedFitProfile, measuredAt: null}
    yield put(setFitProfile(fitSurveyProfile))

    // wait for the result of `setFitProfile`
    const result = yield take([SET_FIT_PROFILE_FAILURE, SET_FIT_PROFILE_SUCCESS])

    yield delay(1000)

    // then handle result of `setFitProfile`
    if (result.type === SET_FIT_PROFILE_SUCCESS) {
      const addDelay = !(yield select(fit.thereWasASuccessfulCalculationDuringThisSession))

      // introduce a delay so the user sees the entire 3, 2, 1 animation
      if (addDelay) yield delay(4000 - (Date.now() - startTime))

      yield put(calculateSuccess())

      // analytics
      const customerType = yield select(cross.customerType)
      const email = yield select(user.email)
      track.completedFitFlow({customerType, email, flow: 'Fit Survey'})

      // send the user to fe-core Sizes page
      window.location.href = URLS.FIT_SIZES
    } else {
      throw new FitCalculateError()
    }
  } catch (e) {
    yield put(calculateFailure(e))
  }
}

export function* calculateSaga() {
  yield takeLatest(CALCULATE, calculateWorker)
}

// Sizes
// ----------------------------------------------------------------------------

function* editSizesWorker() {
  if (!(yield select(cross.canChangeMeasurements))) {
    yield put(
      editSizesFailure(
        new FitCannotChangeMeasurementsError(yield select(events.soonestUpcomingPaidEventDate)),
      ),
    )
    yield put(cancelSizes())
  }
}

export function* editSizesSaga() {
  yield takeLatest(EDIT_SIZES, editSizesWorker)
}

// Modal
// ----------------------------------------------------------------------------

function* displayErrorWorker({payload}) {
  yield put(displayError(payload))
}

export function* displayErrorSaga() {
  yield takeLatest(
    [
      CALCULATE_FAILURE,
      EDIT_SIZES_FAILURE,
      SET_ON_DECK_STEP_FAILURE,
      // add more as they come
    ],
    displayErrorWorker,
  )
}
